import { Component, OnInit } from "@angular/core";
import { ProspectsService } from "../../services/prospects/prospects.service";
import { PlanService } from "../../services/planService/plan.service";
import {PaymentsService} from "../../services/payments/payments.service";
import { BillingService } from "../../services/billing/billing.service";
import { MatTableDataSource } from "@angular/material";
import { Payment } from "../../models/payment";
import { MatDialog } from '@angular/material';
import { AddFilesComponent } from '../../components/marketing/add-files/add-files.component';
import { PaymentsHistoryComponent } from '../../components/payments-history/payments-history.component'
import { CompanyService } from "../../services/company/company.service";
import Swal from "sweetalert2";

@Component({
  selector: "app-payments",
  templateUrl: "./payments.component.html",
  styleUrls: ["./payments.component.scss"],
})
export class PaymentsComponent implements OnInit {

  public dataClients: MatTableDataSource<any>;
  public displayColumns: string[] = [
    'clientNumber',
    'cliente',
    'contacto',
    'plan',
    'monto',
    'formaPago',
    'diaCorte',
    'diaCobro',
    'factura',
    'facturaFile',
    'comprobante',
    'notes',
    'status',
    'acciones'
  ];

  public loader = false;
  public plans = [];
  public searchFilter: string = '';
  public statusFilter: string = '';

  constructor(
    private prospectService: ProspectsService,
    private planService: PlanService,
    private paymentsService: PaymentsService,
    private billingService: BillingService,
    private dialog: MatDialog,
    private companyService: CompanyService,
  ) {}

 async ngOnInit() {
  this.dataClients = new MatTableDataSource<any>();

  this.dataClients.filterPredicate = (data: any, filter: string) => {
    const parsed = JSON.parse(filter);

    const search = (parsed.search || '')
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    const status = (parsed.status || '')
      .toLowerCase();

    const normalizeText = (text: string) => {
      return (text || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    };

    const matchesSearch = 
      normalizeText(data.cliente).includes(search) ||
      normalizeText(data.nombreComercial).includes(search) ||
      normalizeText(data.plan).includes(search) ||
      String(data.clientNumber).includes(search);

    const matchesStatus = !status || 
      (data.status && data.status.toLowerCase() === status);

    return matchesSearch && matchesStatus;
  };

  await this.loadData();  
}

async loadData() {
  this.loader = true;

  try {
    this.plans = (await this.planService.getPlans()).data;

    const response = await this.prospectService.getAllCompleteProspects();

    const activos = response.data.filter(
      p => p.activeInDashboard === true
    );

    const cycles = await this.billingService.getActiveCycles();

    const data = await Promise.all(
      activos.map(async (p) => {

        const cycle = cycles.find(c => {
          const cycleClientId =
            c.clientId?._id ||
            c.clientId?.$oid ||
            c.clientId?.toString?.() ||
            String(c.clientId);

          return cycleClientId === p._id.toString();
        });

        let payment: Payment | null = null;

        if (cycle) {
          const payments = await this.paymentsService.getPaymentsByCycleId(cycle._id);

          payment = payments.find(pay => pay.type === "monthly") || null;
        }

        const cycleStartFormatted = cycle?.startDate 
          ? this.formatInputDate(cycle.startDate) 
          : this.formatInputDate(p.courtDate);

        return {
          clientId: p._id,
          uid: p.uid,
          billingCycleId: cycle?._id,

          hasCycle: !!cycle,

          clientNumber: p.clientNumber || 0,

          cliente: p.nombre,

          formaPago:
            payment?.formaPago ||
            p.formaPago ||
            '',

          nombreComercial: p.nombreCliente,

          contacto:
            p.contacto ||
            payment?.contacto ||
            '',

          plan: p.plan,
          originalPlan: p.plan,

          monto: p.planPrice || 0,
          originalMonto: p.planPrice || 0,

          factura: payment?.invoiceNumber || '',

          diaCorte: cycleStartFormatted,
          cycleStartDate: cycleStartFormatted,

          diaCobro: p.paymentDate
            ? this.formatInputDate(p.paymentDate)
            : this.formatInputDate(p.courtDate),

          originalDiaCorte: cycleStartFormatted,

          originalDiaCobro: p.paymentDate
            ? this.formatInputDate(p.paymentDate)
            : '',

          status: payment?.status || 'pendiente',

          paymentId: payment?._id,

          originalStatus: payment?.status || 'pendiente',

          fileInvoice: payment?.invoiceFile || {
            url: "",
            extension: ""
          },

          paymentProofFiles: payment?.paymentProofFiles || [],

          startDate: cycle?.startDate,
          endDate: cycle?.endDate,

          notes: payment?.notes || ''
        };
      })
    );

    data.sort(
      (a, b) => Number(b.clientNumber || 0) - Number(a.clientNumber || 0)
    );

    this.dataClients.data = data;
    this.dataClients._updateChangeSubscription();

  } catch (error) {
    Swal.fire(
      "Error",
      "No se pudieron cargar los datos",
      "error"
    );
  }

  this.loader = false;
}

  getStatusClass(status: string) {
    return {
      'status-pendiente': status === 'pendiente',
      'status-pagado': status === 'pagado',
      'status-facturado': status === 'facturado',
      'status-suspendido': status === 'suspendido',
      'status-cancelado': status === 'cancelado',
    };
  }

  async removeInvoice(e: any) {
    try {
      const confirm = await Swal.fire({
        title: "Eliminar factura",
        text: "Esta acción no se puede deshacer",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar"
      });

      if (!confirm.isConfirmed) return;

      await this.paymentsService.updatePayment(e.paymentId, {
        invoiceFile: null
      });

      e.fileInvoice = null;

      Swal.fire("OK", "Factura eliminada", "success");

    } catch (error) {
      Swal.fire("Error", "No se pudo eliminar", "error");
    }
  }

  async removeProof(e: any, index: number) {
    try {
      const confirm = await Swal.fire({
        title: "Eliminar comprobante",
        text: "Esta acción no se puede deshacer",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar"
      });

      if (!confirm.isConfirmed) return;

      const updatedFiles = [...e.paymentProofFiles];
      updatedFiles.splice(index, 1);

      await this.paymentsService.updatePayment(e.paymentId, {
        paymentProofFiles: updatedFiles
      });

      e.paymentProofFiles = updatedFiles;

      Swal.fire("OK", "Comprobante eliminado", "success");

    } catch (error) {
      Swal.fire("Error", "No se pudo eliminar", "error");
    }
  }

async saveRow(e) {
  try {
    if (!e.billingCycleId) {
      Swal.fire("Error", "Cliente sin ciclo activo", "error");
      return;
    }

    if (!e.diaCorte || !e.diaCobro) {
      Swal.fire("Error", "Debes ingresar día de corte y día de cobro", "warning");
      return;
    }

    if (!e.monto || e.monto <= 0) {
      Swal.fire("Error", "El monto debe ser mayor a 0", "warning");
      return;
    }

    const montoChanged = Number(e.monto) !== Number(e.originalMonto);
    const planChanged = e.plan !== e.originalPlan;
    const cobroChanged = String(e.diaCobro) !== String(e.originalDiaCobro);
    const corteChanged = String(e.diaCorte) !== String(e.originalDiaCorte);

    const currentProspect = await this.prospectService.getProspectById(e.clientId);
    const isCurrentlyActive = currentProspect?.activeInDashboard === true;

    const statusCancelled = 
      (e.status === 'cancelado' && e.originalStatus !== 'cancelado') || 
      (e.status === 'cancelado' && isCurrentlyActive);

    const oldAmount = e.originalMonto;
    let changeReason = null;
    let clienteDesactivado = false;

    if (montoChanged) {
      const result = await Swal.fire({
        title: "Cambio de monto",
        text: "Debes justificar el cambio de precio",
        input: "textarea",
        inputPlaceholder: "Ej: descuento, cambio de plan, negociación...",
        inputAttributes: { maxlength: "300" },
        showCancelButton: true,
        confirmButtonText: "Guardar",
        cancelButtonText: "Cancelar",
        inputValidator: (value) => {
          if (!value || !value.trim()) return "La justificación es obligatoria";
          if (value.trim().length < 5) return "Debe tener al menos 5 caracteres";
          return null;
        }
      });

      if (!result.isConfirmed) {
        e.monto = e.originalMonto;
        return;
      }

      changeReason = result.value.trim();
    }

    if (cobroChanged || corteChanged) {
      await this.prospectService.updateBillingConfig({
        prospectId: e.clientId,
        courtDate: this.buildUTCDate(e.diaCorte),
        paymentDate: this.buildUTCDate(e.diaCobro)
      });
    } 

    if (montoChanged) {
      await this.prospectService.updatePlanPrice({
        prospectId: e.clientId,
        planPrice: Number(e.monto)
      });
    }

    await this.prospectService.updateProspect(
      e.clientId,
      {
        contacto: e.contacto,
        formaPago: e.formaPago
      }
    );

    if (statusCancelled) {
      const deactivateConfirm = await Swal.fire({
        title: "Desactivar cliente",
        text: "¿Deseas desactivar al cliente del dashboard?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, desactivar",
        cancelButtonText: "No"
      });

      if (deactivateConfirm.isConfirmed) {
        const { value: reason } = await Swal.fire({
          title: "Motivo de cancelación",
          input: "textarea",
          inputLabel: "Indica la razón de desactivación",
          inputPlaceholder: "Ej: Falta de pago, solicitud del cliente, cierre de negocio...",
          inputAttributes: { maxlength: "250" },
          showCancelButton: true,
          confirmButtonText: "Guardar",
          cancelButtonText: "Cancelar",
          inputValidator: (value) => {
            if (!value || !value.trim()) return "Debes ingresar un motivo";
            return null;
          }
        });

        if (reason === undefined || reason === null) {
          e.status = e.originalStatus;
          return;
        }

        await this.companyService.changeStatus(            
          e.uid,
          false,
          reason.trim()
        );

        clienteDesactivado = true;
      }
    }

    const payload = {
      clientId: e.clientId,
      billingCycleId: e.billingCycleId,
      clientNumber: e.clientNumber,

      monto: Number(e.monto),
      extraCharge: 0,

      type: "monthly",
      status: e.status,

      formaPago: e.formaPago,
      contacto: e.contacto,

      invoiceNumber: e.factura,

      plan: e.plan,
      precio: Number(e.monto),
      horasIncluidas: 0,

      notes: e.notes || "",

      changeReason: montoChanged ? changeReason : null,
      previousAmount: montoChanged ? Number(oldAmount) : null,

      origin: "manual"
    };

    if (e.paymentId) {
      await this.paymentsService.updatePayment(e.paymentId, payload);
    } else {
      const res = await this.paymentsService.createPayment(payload);
      e.paymentId = res.insertedId;
    }

    if (montoChanged) e.originalMonto = e.monto;
    if (cobroChanged) e.originalDiaCobro = e.diaCobro;
    if (corteChanged) e.originalDiaCorte = e.diaCorte;
    if (planChanged) e.originalPlan = e.plan;

    e.originalStatus = e.status;

    await Swal.fire("OK", "Pago y configuración guardados", "success");

    if (clienteDesactivado) {
      await this.loadData();
    }

  } catch (error) {
    Swal.fire(
      "Error",
      error.error?.error || "Error al guardar",
      "error"
    );
  }
}

formatDate(date: any): string {
  if (!date) return '';

  const d = new Date(date);
  
  if (isNaN(d.getTime())) return '';

  return d.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  });
}

async createCycle(e: any) {
  try {
    const confirm = await Swal.fire({
      title: "¿Crear ciclo?",
      text: "Se creará el primer ciclo para este cliente",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, crear",
      cancelButtonText: "Cancelar"
    });

    if (!confirm.isConfirmed) return;

    await this.billingService.renewCycle(
      e.clientId,
      "first_cycle"
    );

    Swal.fire("OK", "Ciclo creado correctamente", "success");

    await this.ngOnInit();

  } catch (error) {
    Swal.fire("Error", "No se pudo crear el ciclo", "error");
  }
}

async renewCycle(e: any) {
  try {
    const cycleText = `${this.formatDate(e.startDate)} - ${this.formatDate(e.endDate)}`;

    const corteChanged = String(e.diaCorte) !== String(e.originalDiaCorte);
    const planChanged = e.plan !== e.originalPlan;

    if (!e.paymentId) {
      Swal.fire(
        "Error",
        `El ciclo actual no tiene un pago registrado (${cycleText})`,
        "warning"
      );
      return;
    }

    if (e.status !== 'pagado') {
      Swal.fire(
        "Error",
        `El ciclo actual no está pagado (${cycleText})`,
        "warning"
      );
      return;
    }

    let resetType = "monthly";

    if (corteChanged) {
      const earlyResetAlert = await Swal.fire({
        title: "¿Renovación anticipada?",
        text: "Se detectó un cambio en la fecha de corte. ¿Es una renovación anticipada?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sí, es anticipada",
        cancelButtonText: "No",
        allowOutsideClick: false
      });

      resetType = earlyResetAlert.isConfirmed ? "early_reset" : "monthly";
    }

    const confirm = await Swal.fire({
      title: "¿Renovar ciclo?",
      text: `Se cerrará el ciclo ${cycleText} y se creará uno nuevo`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, renovar",
      cancelButtonText: "Cancelar"
    });

    if (!confirm.isConfirmed) return;

    if (corteChanged && resetType === "early_reset") {
      await this.prospectService.updateBillingConfig({
        prospectId: e.clientId,
        courtDate: this.buildUTCDate(e.diaCorte),
        paymentDate: this.buildUTCDate(e.diaCobro)
      });

      e.originalDiaCorte = e.diaCorte;
      e.originalDiaCobro = e.diaCobro;
    }

    if (planChanged) {
      const confirmPlan = await Swal.fire({
        title: "¿Cambiar plan?",
        text: `Estás cambiando el plan de "${e.originalPlan}" a "${e.plan}"`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, cambiar",
        cancelButtonText: "Cancelar"
      });

      if (!confirmPlan.isConfirmed) {
        e.plan = e.originalPlan;
        return;
      }

      await this.prospectService.updateProspect(
        e.clientId,
        {
          plan: e.plan
        }
      );

      e.originalPlan = e.plan;
    }

    await this.billingService.renewCycle(
      e.clientId,
      resetType
    );

    Swal.fire(
      "OK",
      "Ciclo renovado correctamente",
      "success"
    );

    await this.loadData();

  } catch (error) {
    Swal.fire(
      "Error",
      error?.error?.message ||
      error?.message ||
      "No se pudo renovar el ciclo",
      "error"
    );
  }
}

async addExtraCharge(e: any) {
  try {
    if (!e.billingCycleId) {
      Swal.fire("Error", "El cliente no tiene un ciclo activo para añadir un excedente", "warning");
      return;
    }

    let invoiceFileUploaded: any = null;
    let paymentProofFileUploaded: any = null;

    const { value: formValues } = await Swal.fire({
      title: "Registrar Excedente / Extensión",
      html: `
        <div style="text-align: left; display: flex; flex-direction: column; gap: 12px; font-size: 14px;">
          <div>
            <label style="font-weight: bold; display: block; margin-bottom: 4px;">Horas laborales extra:</label>
            <input id="swal-extra-horas" type="number" min="0" class="swal2-input" style="margin:0; width: 100%;" placeholder="Ej: 5">
          </div>

          <div>
            <label style="font-weight: bold; display: block; margin-bottom: 4px;">Minutos de llamada extra:</label>
            <input id="swal-extra-minutos" type="number" min="0" class="swal2-input" style="margin:0; width: 100%;" placeholder="Ej: 125">
          </div>

          <div>
            <label style="font-weight: bold; display: block; margin-bottom: 4px;">Monto pagado ($):</label>
            <input id="swal-extra-monto" type="number" min="0" class="swal2-input" style="margin:0; width: 100%;" placeholder="Ej: 500">
          </div>

          <div>
            <label style="font-weight: bold; display: block; margin-bottom: 4px;">Número de Factura (Opcional):</label>
            <input id="swal-extra-factura" type="text" class="swal2-input" style="margin:0; width: 100%;" placeholder="Ej: F-12345">
          </div>

          <div>
            <label style="font-weight: bold; display: block; margin-bottom: 4px;">Forma de Pago:</label>
            <select id="swal-extra-forma" class="swal2-select" style="margin:0; width: 100%;">
              <option value="Transferencia">Transferencia</option>
              <option value="Tarjeta">Tarjeta</option>
              <option value="Efectivo">Efectivo</option>
            </select>
          </div>

          <div>
            <label style="font-weight: bold; display: block; margin-bottom: 4px;">Archivos adjuntos (Opcional):</label>
            <div style="display: flex; gap: 8px; margin-top: 4px;">
              <button type="button" id="btn-swal-upload-invoice" class="swal2-confirm swal2-styled" style="margin: 0; padding: 6px 12px; font-size: 12px; background-color: #3366ff;">
                📄 Subir Factura
              </button>
              <button type="button" id="btn-swal-upload-proof" class="swal2-confirm swal2-styled" style="margin: 0; padding: 6px 12px; font-size: 12px; background-color: #00d68f;">
                🧾 Subir Comprobante
              </button>
            </div>
            <div id="swal-files-status" style="margin-top: 6px; font-size: 12px;"></div>
          </div>

          <div>
            <label style="font-weight: bold; display: block; margin-bottom: 4px;">Notas u observaciones:</label>
            <input id="swal-extra-notes" class="swal2-input" style="margin:0; width: 100%;" placeholder="Ej. Extensión autorizada por gerencia">
          </div>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Guardar Excedente",
      cancelButtonText: "Cancelar",
      didOpen: () => {
        const btnInvoice = document.getElementById('btn-swal-upload-invoice');
        const btnProof = document.getElementById('btn-swal-upload-proof');
        const statusDiv = document.getElementById('swal-files-status');

        const updateStatusText = () => {
          let text = '';
          if (invoiceFileUploaded) text += '✓ Factura adjuntada. ';
          if (paymentProofFileUploaded) text += '✓ Comprobante adjuntado.';
          if (statusDiv) statusDiv.innerHTML = text ? `<span style="color: #00d68f; font-weight: bold;">${text}</span>` : '';
        };

        btnInvoice?.addEventListener('click', async () => {
          const swalContainer = (Swal as any).getContainer();
          if (swalContainer) swalContainer.style.visibility = 'hidden';

          const result = await this.dialog.open(AddFilesComponent, {
            data: { title: "Factura" },
            height: '60vh'
          }).beforeClosed().toPromise();

          if (swalContainer) swalContainer.style.visibility = 'visible';

          if (result) {
            invoiceFileUploaded = result;
            updateStatusText();
          }
        });

        btnProof?.addEventListener('click', async () => {
          const swalContainer = (Swal as any).getContainer();
          if (swalContainer) swalContainer.style.visibility = 'hidden';

          const result = await this.dialog.open(AddFilesComponent, {
            data: { title: "Comprobante de pago" },
            height: '60vh'
          }).beforeClosed().toPromise();

          if (swalContainer) swalContainer.style.visibility = 'visible';

          if (result) {
            paymentProofFileUploaded = result;
            updateStatusText();
          }
        });
      },
      preConfirm: () => {
        const extraHours = Number((document.getElementById('swal-extra-horas') as HTMLInputElement)?.value || 0);
        const extraMinutes = Number((document.getElementById('swal-extra-minutos') as HTMLInputElement)?.value || 0);
        const monto = Number((document.getElementById('swal-extra-monto') as HTMLInputElement)?.value || 0);
        const invoiceNumber = (document.getElementById('swal-extra-factura') as HTMLInputElement)?.value || '';
        const formaPago = (document.getElementById('swal-extra-forma') as HTMLSelectElement)?.value;
        const notes = (document.getElementById('swal-extra-notes') as HTMLInputElement)?.value;

        if (monto <= 0) {
          Swal.showValidationMessage("El monto debe ser mayor a $0");
          return false;
        }

        if (extraHours <= 0 && extraMinutes <= 0) {
          Swal.showValidationMessage("Debes ingresar al menos horas o minutos extra");
          return false;
        }

        return {
          extraHours,
          extraMinutes,
          monto,
          invoiceNumber,
          formaPago,
          notes
        };
      }
    });

    if (!formValues) return;

    const payload: any = {
      clientId: e.clientId,
      billingCycleId: e.billingCycleId,
      clientNumber: e.clientNumber,

      type: "extra",
      monto: formValues.monto,
      precio: formValues.monto,

      invoiceNumber: formValues.invoiceNumber,

      extraHours: formValues.extraHours,
      extraMinutes: formValues.extraMinutes,

      formaPago: formValues.formaPago,
      contacto: e.contacto,
      plan: e.plan,
      status: "pagado",

      notes: formValues.notes || `Excedente: +${formValues.extraHours} hrs laborales / +${formValues.extraMinutes} min llamadas`,
      origin: "manual"
    };

    if (invoiceFileUploaded) {
      payload.invoiceFile = invoiceFileUploaded;
    }

    if (paymentProofFileUploaded) {
      payload.paymentProofFiles = [{
        ...paymentProofFileUploaded,
        uploadedAt: new Date()
      }];
    }

    await this.paymentsService.createPayment(payload);

    Swal.fire("OK", "Excedente registrado correctamente", "success");
    await this.loadData();

  } catch (error) {
    Swal.fire("Error", "No se pudo registrar el excedente", "error");
  }
}

async uploadFile(e: any, type: string) {
  try {

    if (!e.paymentId) {
      Swal.fire("Error", "Primero debes guardar el pago", "warning");
      return;
    }

    const result = await this.dialog.open(AddFilesComponent, {
      data: { title: type },
      height: '60vh'
    }).beforeClosed().toPromise();

    if (!result) return;

    let update: any = {};

    if (type === "Factura") {
      update.invoiceFile = result;
      e.fileInvoice = result;
    }

    if (type === "Comprobante de pago") {
      update.newPaymentProof = result;

      if (!e.paymentProofFiles) {
        e.paymentProofFiles = [];
      }

      e.paymentProofFiles.push({
        ...result,
        uploadedAt: new Date()
      });
    }

    await this.paymentsService.updatePayment(e.paymentId, update);

    Swal.fire("OK", "Archivo subido correctamente", "success");

  } catch (error) {
    Swal.fire("Error", "No se pudo subir el archivo", "error");
  }
}

viewFile(url: string) {
  window.open(url, '_blank');
}

applyFilter(event: any) {
  let searchValue = (event.target.value || '').trim().toLowerCase();
  
  
  searchValue = searchValue.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  
  this.searchFilter = searchValue;
  this.applyCombinedFilter();
}

applyStatusFilter(status: string) {

  console.log("Applying status filter:", status);
  this.statusFilter = status;

  this.applyCombinedFilter();
}

applyCombinedFilter() {
  this.dataClients.filter = JSON.stringify({
    search: this.searchFilter,
    status: this.statusFilter
  });

  this.dataClients._updateChangeSubscription();
}

async viewHistory(e: any) {

  const dialogRef = this.dialog.open(
    PaymentsHistoryComponent,
    {
      data: {
        clientId: e.clientId,
        clientName: e.cliente
      },
      width: '95vw',
      height: '92vh',
      maxWidth: '95vw',
      maxHeight: '92vh',
      panelClass: 'full-screen-modal'
    }
  );

  dialogRef.afterClosed()
    .subscribe(async result => {

      if (result?.refresh) {

        await this.loadData();
      }
    });
}

formatInputDate(date: any) {

  if (!date) return '';

  return new Date(date)
    .toISOString()
    .split('T')[0];
}

buildUTCDate(dateString: string): Date {

  const [year, month, day] = dateString
    .split('-')
    .map(Number);

  return new Date(Date.UTC(year, month - 1, day));
}

}